import type { CanvasExonData, CanvasRangeData } from "./type";
import type { KonvaRenderData } from "@/assets/js/konva";

interface DisposeOption {
    unionset: CanvasExonData[];
    offsetX: number;
    offsetY: number;
    space: number;
    ratio: number;
}

interface DisposeExonOption extends DisposeOption {
    exon: CanvasExonData;
}

//* 获取与上一个外显子之间的距离
export const disposeUpExonDistance = (cur: CanvasExonData, up: CanvasExonData, unionset: CanvasRangeData[]) => {
    //* 初始化数据
    const result = {
        number: 0, //* 两个外显子之间经过的区域数量
        size: cur.start - up.end - 1, //* 两个外显子之间经过的区域不计间隔的尺寸
    };

    //* 计算经过的区域
    const start = up.end + 1;
    const end = cur.start - 1;
    for (let i = 0; i < unionset.length; i++) {
        if (unionset[i].start >= start) {
            result.number++;
            if (unionset[i].start >= end) {
                return result;
            }
        }
    }

    return result;
};

//* 通过相对坐标计算真实坐标
export const disposeRelatedToReal = (site: number, option: { space: number; align?: string; ratio: number; unionset: CanvasExonData[] }) => {
    //* 初始化数据
    let result = 0;
    for (let i = 0; i < option.unionset.length; i++) {
        if (option.unionset[i].start <= site && option.unionset[i].end >= site) {
            result += (site - option.unionset[i].start) * option.ratio;
            break;
        } else {
            result += option.space + (option.unionset[i].end - option.unionset[i].start + 1) * option.ratio;
        }
    }

    switch (option.align) {
        case "center":
            return result + option.ratio / 2;

        case "right":
            return result + option.ratio;

        default:
            return result;
    }
};

//* 获取内含子配置
export const disposeIntronOption = (x: number, y: number, size: number): KonvaRenderData => {
    //* 路径初始化
    const offsetX = x - size;
    const data = [`M${offsetX} ${y + 16}`, `L${offsetX + size / 2} ${y + 6}`, `L${offsetX + size} ${y + 16}`];

    //* 添加进绘制队列中
    return {
        el: "Path",
        attrs: [
            { attrName: "data", attrContext: data.join(" ") },
            { attrName: "stroke", attrContext: "#666666" },
            { attrName: "strokeWidth", attrContext: 1 },
        ],
    };
};

//* 获取外显子配置
export const disposeExonOption = (option: DisposeExonOption) => {
    //* 外显子
    const exonEnd = disposeRelatedToReal(option.exon.end, { align: "right", ...option });
    const exonStart = disposeRelatedToReal(option.exon.start, option);
    const size = exonEnd - exonStart;
    const result: KonvaRenderData = {
        el: "Group",
        width: size,
        attrs: [
            { attrName: "x", attrContext: option.offsetX + exonStart },
            { attrName: "y", attrContext: option.offsetY },
        ],
        children: [
            {
                el: "Rect",
                attrs: [
                    { attrName: "fill", attrContext: "#00C7C8" },
                    { attrName: "width", attrContext: size },
                    { attrName: "stroke", attrContext: "#00C7C8" },
                    { attrName: "height", attrContext: 24 },
                    { attrName: "strokeWidth", attrContext: 1 },
                ],
            },
        ],
    };

    //* UTR区域
    if (option.exon.utr) {
        const uEnd = disposeRelatedToReal(option.exon.utr.end, { align: "right", ...option });
        const uStart = disposeRelatedToReal(option.exon.utr.start, option);
        result.children?.push({
            el: "Rect",
            attrs: [
                { attrName: "fill", attrContext: "#ffffff" },
                { attrName: "width", attrContext: uEnd - uStart },
                { attrName: "height", attrContext: 23 },
                { attrName: "x", attrContext: uStart - exonStart },
            ],
        });
    }

    return result;
};
